import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@/lib/navigation";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Brand } from "@/components/brand";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@/components/ui/primitives";
import { toast } from "@/components/ui/toaster";
import { changePassword } from "@/lib/api/endpoints";
import { PASSWORD_RULES, firstPasswordFailure, passwordMeetsPolicy } from "@/lib/password-policy";
import { qk } from "@/lib/query/keys";

const schema = z
  .object({
    current_password: z.string().min(1, "Enter your current password"),
    // One rule, sourced from lib/password-policy so the message and the
    // checklist below can never disagree.
    new_password: z.string().refine(passwordMeetsPolicy, (v) => ({
      message: `Must contain: ${firstPasswordFailure(v)}`,
    })),
    confirm: z.string().min(1, "Confirm your new password"),
  })
  .refine((v) => v.new_password === v.confirm, {
    path: ["confirm"],
    message: "Passwords do not match",
  })
  .refine((v) => v.new_password !== v.current_password, {
    path: ["new_password"],
    message: "New password must differ from the current one",
  });

export default function ChangePasswordPage() {
  const router = useRouter();
  const qc = useQueryClient();
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { current_password: "", new_password: "", confirm: "" },
  });

  const mutation = useMutation({
    mutationFn: (v: z.infer<typeof schema>) => changePassword(v.current_password, v.new_password),
    onSuccess: async () => {
      toast.success("Password changed.");
      await qc.invalidateQueries({ queryKey: qk.me });
      router.replace("/dashboard");
      router.refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <main className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <Brand variant="stacked" />
        </div>
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle>Set a new password</CardTitle>
            <CardDescription>
              For security, choose a new password before continuing.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={form.handleSubmit((v) => mutation.mutate(v))}>
              <div className="space-y-1.5">
                <Label>Current (temporary) password</Label>
                <Input
                  type="password"
                  autoComplete="current-password"
                  {...form.register("current_password")}
                />
                {form.formState.errors.current_password && (
                  <p className="text-xs text-destructive">
                    {form.formState.errors.current_password.message}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>New password</Label>
                <Input
                  type="password"
                  autoComplete="new-password"
                  {...form.register("new_password")}
                />
                <PolicyChecklist value={form.watch("new_password") ?? ""} />
                {form.formState.errors.new_password && (
                  <p className="text-xs text-destructive">
                    {form.formState.errors.new_password.message}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Confirm new password</Label>
                <Input type="password" autoComplete="new-password" {...form.register("confirm")} />
                {form.formState.errors.confirm && (
                  <p className="text-xs text-destructive">
                    {form.formState.errors.confirm.message}
                  </p>
                )}
              </div>
              <Button type="submit" className="w-full" disabled={mutation.isPending}>
                {mutation.isPending ? "Saving…" : "Change password"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}

/** Live rule checklist. Shown from the first keystroke rather than only after a
 *  failed submit — the point is to guide the choice, not to grade it. */
function PolicyChecklist({ value }: { value: string }) {
  return (
    <ul className="space-y-1 pt-1" aria-label="Password requirements">
      {PASSWORD_RULES.map((rule) => {
        const met = rule.test(value);
        return (
          <li
            key={rule.label}
            className={`flex items-center gap-1.5 text-xs ${
              met ? "text-[hsl(var(--success))]" : "text-muted-foreground"
            }`}
          >
            {/* aria-hidden: the state is already conveyed by the list item's
                text plus aria-checked below, so the glyph is decorative. */}
            <span aria-hidden="true">{met ? "✓" : "○"}</span>
            <span role="checkbox" aria-checked={met}>
              {rule.label}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
